import { useMemo } from "react";

interface MarketFilterProps {
  markets: string[];
  counts: Record<string, number>;
  selectedMarkets: Set<string>;
  onToggle: (market: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
}

export function MarketFilter({
  markets,
  counts,
  selectedMarkets,
  onToggle,
  onSelectAll,
  onClear,
}: MarketFilterProps) {
  const ordered = useMemo(() => {
    const rest = markets.filter((m) => m !== "Global").sort();
    return markets.includes("Global") ? ["Global", ...rest] : rest;
  }, [markets]);

  const hasSelection = selectedMarkets.size > 0;

  return (
    <aside
      className="shrink-0"
      style={{
        width: 260,
        background: "var(--opella-sidebar-bg)",
        borderRight: "1px solid var(--opella-sidebar-border)",
      }}
      aria-label="Market filter"
    >
      <div className="sticky top-0 flex flex-col gap-5 px-6 py-8">
        <div className="flex items-center justify-between">
          <h2
            className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: "var(--opella-fg-secondary)" }}
          >
            Markets
          </h2>
          <div className="flex items-center gap-3 text-xs">
            <button
              type="button"
              onClick={onSelectAll}
              className="font-medium hover:underline"
              style={{ color: "var(--opella-accent)" }}
            >
              Select all
            </button>
            <span style={{ color: "var(--opella-sidebar-border)" }}>|</span>
            <button
              type="button"
              onClick={onClear}
              disabled={!hasSelection}
              className="font-medium hover:underline disabled:opacity-40"
              style={{ color: "var(--opella-muted)" }}
            >
              Clear
            </button>
          </div>
        </div>

        <ul className="flex flex-col gap-1" role="group" aria-label="Markets">
          {ordered.map((market) => {
            const id = `market-${market.toLowerCase().replace(/\s+/g, "-")}`;
            const checked = selectedMarkets.has(market);
            const count = counts[market] ?? 0;

            return (
              <li key={market}>
                <label
                  htmlFor={id}
                  className="hover:bg-white flex cursor-pointer items-center justify-between rounded-[10px] px-3 py-2 transition-colors"
                  style={{ color: "var(--opella-fg)" }}
                >
                  <span className="flex items-center gap-3">
                    <input
                      id={id}
                      type="checkbox"
                      className="size-4 cursor-pointer rounded"
                      style={{ accentColor: "var(--opella-accent)" }}
                      checked={checked}
                      onChange={() => onToggle(market)}
                      aria-label={`Filter by ${market}`}
                    />
                    <span className="text-sm font-medium">{market}</span>
                  </span>
                  <span
                    className="text-xs tabular-nums"
                    style={{ color: "var(--opella-muted)" }}
                    aria-hidden
                  >
                    {count}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {!hasSelection ? (
          <p
            className="mt-2 text-xs leading-relaxed"
            style={{ color: "var(--opella-muted)" }}
          >
            No markets selected — showing every video.
          </p>
        ) : null}
      </div>
    </aside>
  );
}
